import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { Badge, Chargement, EnTetePage, Erreur, Infobulle } from '../components/Elements';
import { formaterDate, formaterDuree, formaterHeure, libelleJour } from '../lib/format';
import type { FeuillePresence, LigneFeuillePresence, StatutPresence } from '../types';

/**
 * FEUILLE DE PRESENCE d'un creneau (2026-10-13, lot 2) — maquette validee
 * par Georges : une ligne par inscrit, une colonne par seance du jour du
 * creneau, un total d'absences par ligne, un bilan par colonne.
 *
 * Cinq etats par case : present, absent, non saisi, a venir, hors
 * inscription (avant un demarrage differe, apres une fin anticipee).
 * CHAQUE CLIC ENREGISTRE — pas de bouton : la feuille se remplit en
 * passant, et le journal d'audit garde qui a pointe quoi. Le mode vient
 * du type d'activite : « presence » fait tourner non saisi -> present ->
 * absent ; « absence seulement » ne pose que l'absence, une case vide
 * vaut present.
 */
type Etat = StatutPresence | 'vide' | 'futur' | 'hors';

const STYLES: Record<Etat, string> = {
  present: 'border-emerald-200 bg-emerald-50 text-emerald-600',
  absent: 'border-red-200 bg-red-50 text-red-600',
  vide: 'border-dashed border-encre-300 bg-white text-encre-400',
  futur: 'border-encre-100 bg-encre-50 text-encre-300',
  hors: 'border-encre-200 bg-encre-100 text-encre-400',
};
const GLYPHES: Record<Etat, string> = { present: '✓', absent: '✕', vide: '', futur: '', hors: '—' };

function horsInscription(ligne: LigneFeuillePresence, date: string): boolean {
  if (ligne.demarrage_differe && ligne.date_debut && date < ligne.date_debut) return true;
  if (ligne.date_fin_anticipee && date > ligne.date_fin_anticipee) return true;
  return false;
}

export function PresencesPage() {
  const { id } = useParams<{ id: string }>();
  const { peut } = useAuth();
  const feuille = useRessource(
    () => api.get<FeuillePresence>(`/api/creneaux/${id}/presences`),
    [id],
  );
  const [erreur, setErreur] = useState<string | null>(null);
  // Les pointages tels qu'affiches : la feuille chargee, corrigee au fil
  // des clics sans attendre un rechargement complet.
  const [local, setLocal] = useState<Record<string, Record<string, StatutPresence>>>({});
  const [enCours, setEnCours] = useState(false);

  if (feuille.chargement && !feuille.donnees) return <Chargement />;
  if (feuille.erreur || !feuille.donnees) {
    return (
      <Erreur message={feuille.erreur ?? 'Feuille introuvable.'} onReessayer={feuille.recharger} />
    );
  }
  const { creneau, mode, dates, lignes } = feuille.donnees;
  const peutSaisir = peut('presence.saisir') && mode !== 'aucun';

  const pointageDe = (ligne: LigneFeuillePresence, date: string): StatutPresence | undefined => {
    const corrige = local[ligne.inscription_id];
    if (corrige && date in corrige) return corrige[date];
    return ligne.presences[date];
  };

  const etatDe = (
    ligne: LigneFeuillePresence,
    colonne: { date: string; passee: boolean },
  ): Etat => {
    if (horsInscription(ligne, colonne.date)) return 'hors';
    if (!colonne.passee) return 'futur';
    const pointe = pointageDe(ligne, colonne.date);
    if (pointe) return pointe;
    // En mode « absence seulement », une case vide VAUT present.
    return mode === 'absence' ? 'present' : 'vide';
  };

  const pointer = async (
    ligne: LigneFeuillePresence,
    date: string,
    statut: StatutPresence | null,
  ) => {
    setErreur(null);
    setEnCours(true);
    try {
      await api.put('/api/presences', {
        inscriptionId: ligne.inscription_id,
        dateSeance: date,
        statut,
      });
      setLocal((avant) => ({
        ...avant,
        [ligne.inscription_id]: {
          ...(avant[ligne.inscription_id] ?? {}),
          [date]: statut as StatutPresence,
        },
      }));
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
      feuille.recharger();
    } finally {
      setEnCours(false);
    }
  };

  /** Le cycle d'un clic, selon le mode. */
  const suivant = (ligne: LigneFeuillePresence, date: string): StatutPresence | null => {
    const pointe = pointageDe(ligne, date) ?? null;
    if (mode === 'absence') return pointe === 'absent' ? null : 'absent';
    if (pointe === null) return 'present';
    if (pointe === 'present') return 'absent';
    return null;
  };

  const auClic = (ligne: LigneFeuillePresence, colonne: { date: string; passee: boolean }) => {
    if (!peutSaisir || enCours) return;
    const etat = etatDe(ligne, colonne);
    if (etat === 'hors' || etat === 'futur') return;
    void pointer(ligne, colonne.date, suivant(ligne, colonne.date));
  };

  /** « tous ✓ » : les cases non saisies de la colonne passent a present. */
  const tousPresents = async (date: string) => {
    if (!peutSaisir || enCours) return;
    setErreur(null);
    setEnCours(true);
    try {
      for (const ligne of lignes) {
        if (horsInscription(ligne, date) || pointageDe(ligne, date)) continue;
        await api.put('/api/presences', {
          inscriptionId: ligne.inscription_id,
          dateSeance: date,
          statut: 'present',
        });
        setLocal((avant) => ({
          ...avant,
          [ligne.inscription_id]: { ...(avant[ligne.inscription_id] ?? {}), [date]: 'present' },
        }));
      }
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
      feuille.recharger();
    } finally {
      setEnCours(false);
    }
  };

  const absencesDe = (ligne: LigneFeuillePresence) =>
    dates.filter((c) => etatDe(ligne, c) === 'absent').length;
  const bilanDe = (colonne: { date: string; passee: boolean }) => {
    if (!colonne.passee) return '';
    let presents = 0;
    let absents = 0;
    for (const ligne of lignes) {
      const etat = etatDe(ligne, colonne);
      if (etat === 'present') presents += 1;
      if (etat === 'absent') absents += 1;
    }
    return `${presents} · ${absents}`;
  };

  const passees = dates.filter((c) => c.passee).length;
  const encadrants = creneau.encadrants.map((e) => e.prenom_usuel).join(' + ');
  const libelleMode =
    mode === 'presence' ? 'Présence' : mode === 'absence' ? 'Absence seulement' : 'Non applicable';

  return (
    <div>
      <EnTetePage
        titre={
          <>
            Présences — {creneau.activite_libelle} · {libelleJour(creneau.jour)}{' '}
            {formaterHeure(creneau.heure_debut)}{' '}
            <span className="font-normal text-encre-500">
              ({formaterDuree(creneau.duree_minutes)})
            </span>
          </>
        }
        description={[
          encadrants || null,
          creneau.lieu_code,
          `${lignes.length} inscrit(s) · ${passees} séance(s) passée(s) sur ${dates.length}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <Link to="/planning" className="btn-secondaire">
            ← Retour au planning
          </Link>
        }
      />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-encre-700">
        <div className="flex items-center gap-2">
          <span>Suivi</span>
          <Badge ton="contour">{libelleMode}</Badge>
          {mode === 'absence' && (
            <span className="text-encre-500">
              — une case vide vaut présent, un clic ne pose que l’absence.
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Legende etat="present" libelle="présent" />
          <Legende etat="absent" libelle="absent" />
          {mode !== 'absence' && <Legende etat="vide" libelle="non saisi" />}
          <Legende etat="futur" libelle="à venir" />
          <Legende etat="hors" libelle="hors inscription" />
        </div>
      </div>

      {erreur && (
        <div className="mb-3">
          <Erreur message={erreur} />
        </div>
      )}

      {dates.length === 0 ? (
        <p className="text-sm text-encre-500">
          Aucune séance au calendrier pour le {libelleJour(creneau.jour).toLowerCase()} de cette
          année.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-encre-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-encre-50">
                <th className="sticky left-0 z-10 w-56 min-w-56 border-b border-r border-encre-200 bg-encre-50 px-3 py-2 text-left font-medium">
                  Adhérent
                </th>
                {dates.map((colonne) => (
                  <th
                    key={colonne.date}
                    className={`w-12 min-w-12 border-b border-encre-200 px-0.5 py-1.5 text-center font-medium ${
                      colonne.passee ? 'text-encre-900' : 'text-encre-400'
                    }`}
                  >
                    <div className="tabulaire">{formaterDate(colonne.date).slice(0, 5)}</div>
                    {colonne.passee && peutSaisir && mode === 'presence' && (
                      <Infobulle texte={`Tous présents le ${formaterDate(colonne.date)}`} className="inline-flex">
                        <button
                          type="button"
                          className="mt-0.5 rounded border border-encre-200 bg-white px-1 text-[10px] leading-4 text-encre-500 hover:border-encre-400 hover:text-encre-900"
                          onClick={() => void tousPresents(colonne.date)}
                        >
                          tous ✓
                        </button>
                      </Infobulle>
                    )}
                  </th>
                ))}
                <th className="w-14 min-w-14 border-b border-l border-encre-200 px-2 py-2 text-right font-medium">
                  Abs.
                </th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne) => {
                const absences = absencesDe(ligne);
                return (
                  <tr key={ligne.inscription_id}>
                    <th className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-encre-100 bg-white px-3 py-1.5 text-left font-normal">
                      <span className="flex items-baseline gap-1.5">
                        <Link
                          to={`/adherents/${ligne.personne_id}`}
                          className="font-medium text-encre-900 hover:underline"
                        >
                          {ligne.nom.toUpperCase()} {ligne.prenom}
                        </Link>
                        {ligne.forfait_essai && <Badge ton="contour">essai</Badge>}
                        {ligne.demarrage_differe && ligne.date_debut && (
                          <Badge>depuis le {formaterDate(ligne.date_debut)}</Badge>
                        )}
                        {ligne.date_fin_anticipee && (
                          <Badge ton="danger">
                            arrêt le {formaterDate(ligne.date_fin_anticipee)}
                          </Badge>
                        )}
                      </span>
                    </th>
                    {dates.map((colonne) => {
                      const etat = etatDe(ligne, colonne);
                      const saisissable = peutSaisir && etat !== 'hors' && etat !== 'futur';
                      return (
                        <td
                          key={colonne.date}
                          className="border-b border-encre-100 px-0.5 py-1 text-center"
                        >
                          <button
                            type="button"
                            disabled={!saisissable}
                            aria-label={`${ligne.prenom} ${ligne.nom}, ${formaterDate(colonne.date)} : ${etat}`}
                            className={`h-7 w-10 rounded border text-sm leading-none ${STYLES[etat]} ${
                              saisissable ? 'hover:border-encre-400' : 'cursor-default'
                            }`}
                            onClick={() => auClic(ligne, colonne)}
                          >
                            {GLYPHES[etat]}
                          </button>
                        </td>
                      );
                    })}
                    <td
                      className={`tabulaire border-b border-l border-encre-100 px-2 py-1.5 text-right ${
                        absences > 0 ? 'text-red-600' : 'text-encre-400'
                      }`}
                    >
                      {absences}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-encre-50">
                <th className="sticky left-0 z-10 border-r border-t border-encre-200 bg-encre-50 px-3 py-2 text-left font-medium text-encre-500">
                  Présents · absents
                </th>
                {dates.map((colonne) => (
                  <td
                    key={colonne.date}
                    className="tabulaire border-t border-encre-200 px-0.5 py-2 text-center text-encre-500"
                  >
                    {bilanDe(colonne)}
                  </td>
                ))}
                <td className="border-l border-t border-encre-200" />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="mt-3 text-xs text-encre-500">
        {mode === 'absence'
          ? 'Un clic pose l’absence, un second l’efface. Chaque clic est enregistré aussitôt.'
          : 'Un clic passe de non saisi à présent, puis absent, puis non saisi. Chaque clic est enregistré aussitôt.'}{' '}
        Les séances à venir et les cases hors inscription ne se saisissent pas.
      </p>
    </div>
  );
}

function Legende({ etat, libelle }: { etat: Etat; libelle: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded border text-xs leading-none ${STYLES[etat]}`}
      >
        {GLYPHES[etat]}
      </span>
      {libelle}
    </span>
  );
}
